/**
 * SE-6490 / SE-6866 — the pipeline card's footer (Player Card v7, row 5).
 *
 * @remarks
 * The design's footer reads `Updated 2d ago · Hugo`: the card's last-updated
 * relative time, then the person who made that update, in muted text.
 *
 * **SE-6490 shipped the time alone because the schema had no honest source for
 * the second half; SE-6866 supplied one.** Back then `KanbanCard` exposed
 * `createdBy`/`creator` — who ADDED the player to the board — and no
 * last-updater field, so naming them would have asserted something false about
 * who changed the card. `KanbanCard.lastEditedByUserId` now records the last
 * HUMAN editor, and the "gains one prop" that comment anticipated is
 * {@link PlayerCardFooterProps.editorName}, resolved to a display name upstream
 * (`useCardEditorName`) so this component stays presentational.
 *
 * Both degradations are specified, not incidental: an absent or unresolvable
 * editor renders the time by itself, while an absent or unparseable TIMESTAMP
 * hides the whole row even when the editor is known — a name with no recency
 * is not what this footer is for. The rule lives in `formatCardUpdatedFooter`
 * and is unit-tested there.
 *
 * The name is the roster's full display name (given + family, falling back to
 * email); the design mocks a first name only, and `numberOfLines={1}` truncates
 * whatever does not fit. A full name is what disambiguates two Hugos.
 * @module components/organisms/custom/ui/PlayerCardFooter/PlayerCardFooterContainer
 */
import React from "react";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { formatCardUpdatedFooter } from "@/features/player-kanban/utils/cardUpdatedAgo";
import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/** Props for PlayerCardFooter */
interface PlayerCardFooterProps {
  /** The backing kanban card's `updatedAt`; absent/unparseable hides the row */
  readonly updatedAt?: string | null;
  /**
   * SE-6866: display name of the card's last human editor, already resolved
   * from `lastEditedByUserId`; absent renders the time alone.
   */
  readonly editorName?: string | null;
}

/**
 * Renders the muted "Updated …" footer, or nothing when the time is unknown.
 * @param root0 - Component props
 * @param root0.updatedAt - The card's last-updated ISO timestamp
 * @param root0.editorName - SE-6866: the last editor's resolved display name
 */
const PlayerCardFooter = ({ updatedAt, editorName }: PlayerCardFooterProps) => {
  const label = formatCardUpdatedFooter(updatedAt, editorName);
  if (!label) return null;

  return (
    <Stack direction="horizontal" className="items-center justify-end">
      <Text
        variant="micro"
        numberOfLines={1}
        className="text-content-secondary"
        testID={TEST_ID_PLAYER_KANBAN.PLAYER_CARD_UPDATED}
      >
        {label}
      </Text>
    </Stack>
  );
};

export default PlayerCardFooter;
